import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addReportTable } from "reporting/pdf/addReportTable";
import {
  addKeyValueBlock,
  addSectionTitle,
  ensurePageSpace,
} from "reporting/pdf/pdfLayout";
import {
  fmtCurrency,
  fmtDate,
  fmtNumber,
  safeFilePart,
} from "reporting/pdf/pdfFormatters";
import {
  addStructurerScheduleChart,
  addStructurerSufficiencyChart,
} from "./addStructurerCharts";
import { renderFundPlannerDisclosurePdf } from "./renderFundPlannerDisclosurePdf";

const DRAW_COLUMNS = [
  { key: "drawDate", label: "Date", format: fmtDate },
  { key: "drawRequirement", label: "Requirement", align: "right", format: (v) => fmtCurrency(v, 2) },
];
const INVESTMENT_COLUMNS = [
  { key: "type", label: "Type" },
  { key: "name", label: "Security" },
  { key: "maturityDate", label: "Maturity", format: fmtDate },
  { key: "firstInterestPaymentDate", label: "First Interest", format: (v) => v ? fmtDate(v) : "" },
  { key: "ratePct", label: "Rate", align: "right", format: (v) => `${fmtNumber(v, 3)}%` },
  { key: "price", label: "Price", align: "right", format: (v) => fmtNumber(v, 3) },
  { key: "principal", label: "Principal", align: "right", format: (v) => fmtCurrency(v, 2) },
  { key: "totalInterest", label: "Interest", align: "right", format: (v) => fmtCurrency(v, 2) },
  { key: "cost", label: "Cost", align: "right", format: (v) => fmtCurrency(v, 2) },
];
const SUFFICIENCY_COLUMNS = [
  { key: "date", label: "Date", format: fmtDate },
  { key: "type", label: "Type" },
  { key: "description", label: "Description" },
  { key: "netChange", label: "Net Change", align: "right", format: (v) => fmtCurrency(v, 2) },
  { key: "balance", label: "Balance", align: "right", format: (v) => fmtCurrency(v, 2) },
];
const UST_COLUMNS = [
  { key: "cashFlowDate", label: "Date", format: fmtDate },
  { key: "interpolatedYieldPct", label: "Interp Yield", align: "right", format: (v) => `${fmtNumber(v, 5)}%` },
  { key: "yearFraction", label: "Year Frac", align: "right", format: (v) => fmtNumber(v, 7) },
  { key: "discountFactor", label: "Disc Factor", align: "right", format: (v) => fmtNumber(v, 9) },
  { key: "presentValue", label: "Hypothetical Cost", align: "right", format: (v) => fmtCurrency(v, 2) },
];
const CUSTOM_CURVE_COLUMNS = [
  { key: "date", label: "Date", format: fmtDate },
  { key: "yieldPct", label: "Yield", align: "right", format: (v) => `${fmtNumber(v, 6)}%` },
];

const FUND_TYPE_LABELS = {
  project: "Project Fund",
  refunding: "Refunding Deposit",
  capint: "Capitalized Interest",
  dsf: "Debt Service Fund (P&I)",
  reserve: "Debt Service Reserve",
};

function percent(value) {
  return value == null || !Number.isFinite(Number(value)) ? "—" : `${fmtNumber(value, 6)}%`;
}

export async function exportStructurerPdf({ report, orgId = null, save = true } = {}) {
  const hypotheticalPortfolio = report?.hypotheticalPortfolio || report?.usTreasury || null;
  const isHypothetical = Boolean(hypotheticalPortfolio?.isHypothetical);
  const ctx = await createCompassPdf({
    orgId,
    reportTitle: "FundPlanner - Structurer Report",
    orgName: report?.orgName || report?.fundName || "Current Fund",
    asOfLabel: report?.asOfDate && report?.selectedCurve !== "custom"
      ? `Market as of ${fmtDate(report.asOfDate)}`
      : "",
    createdStamp: new Date(),
    orientation: "portrait",
    branding: "unbranded",
    contentMargins: { bottom: 58 },
  });
  const { doc, margins, usableWidth } = ctx;
  let y = margins.top + 2;
  y = addKeyValueBlock(ctx, [
    { label: "Fund", value: report?.fundName || "Current Fund" },
    { label: "Fund Type", value: FUND_TYPE_LABELS[report?.fundType] || report?.fundType || "—" },
    ...(isHypothetical ? [] : [
      { label: "Funding Mode", value: report?.fundMode === "net" ? "Net Fund (Cost Min)" : "Gross Fund (Yield Max)" },
      { label: "Settlement", value: report?.settlementDate ? fmtDate(report.settlementDate) : "—" },
    ]),
    { label: "Requirement", value: fmtCurrency(report?.totalAmount, 2) },
    { label: "Investment Universe", value: isHypothetical ? hypotheticalPortfolio.universeLabel : "SLGS" },
  ], y, {
    columns: 2,
    labelWidth: isHypothetical ? 100 : 92,
    columnGap: isHypothetical ? 24 : 12,
  });

  if (isHypothetical) {
    const summary = hypotheticalPortfolio.summary || {};
    y = addSectionTitle(ctx, "Summary", y);
    y = addKeyValueBlock(ctx, [
      { label: "Total Cash Outflows", value: fmtCurrency(summary.totalGrossCashFlows, 2) },
      { label: "Weighted Avg Interpolated Yield", value: percent(summary.weightedAverageInterpolatedYieldPct) },
      { label: "Number of Cash Flows", value: fmtNumber(summary.cashFlowCount, 0) },
      { label: "Earliest Cash Flow", value: summary.earliestCashFlowDate ? fmtDate(summary.earliestCashFlowDate) : "—" },
      { label: "Latest Cash Flow", value: summary.latestCashFlowDate ? fmtDate(summary.latestCashFlowDate) : "—" },
      { label: "Weighted Average Life", value: `${fmtNumber(summary.weightedAverageLife, 3)} years` },
    ], y, { columns: 2, labelWidth: 145, rowHeight: 16, spaceAfter: 4 });

    y = addSectionTitle(ctx, "Performance", y, { spaceBefore: 12 });
    y = addKeyValueBlock(ctx, [
      { label: "Hypothetical Portfolio Cost (NPV)", value: fmtCurrency(summary.portfolioCost, 2) },
      { label: "Total Discount", value: fmtCurrency(summary.totalDiscount, 2) },
      { label: "Effective Portfolio Yield", value: percent(summary.portfolioXirrPct) },
    ], y, { columns: 2, labelWidth: 145, rowHeight: 16, spaceAfter: 4 });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    const note = hypotheticalPortfolio.methodologyNote;
    const lines = doc.splitTextToSize(note, usableWidth);
    const noteY = y + 8;
    doc.text(lines, margins.left, noteY);
    y = noteY + lines.length * 9 + 10;
    y = addReportTable(ctx, { title: "Requirements", columns: DRAW_COLUMNS, rows: report?.draws || [] }, y);
    if (hypotheticalPortfolio.universe === "custom") {
      const curveMetadata = [
        hypotheticalPortfolio.curveAsOfDate
          ? { label: "As of Date", value: fmtDate(hypotheticalPortfolio.curveAsOfDate) }
          : null,
        hypotheticalPortfolio.curveDescription
          ? { label: "Description", value: hypotheticalPortfolio.curveDescription }
          : null,
      ].filter(Boolean);
      // Keep the section heading, metadata, table header, and first row together.
      // Unlike autoTable, the heading and key/value block do not paginate themselves.
      y = ensurePageSpace(ctx, y, 80 + curveMetadata.length * 16);
      y = addSectionTitle(ctx, "User-Defined Curve Inputs", y);
      if (curveMetadata.length) {
        y = addKeyValueBlock(ctx, curveMetadata, y, {
          columns: 1,
          labelWidth: 90,
          rowHeight: 16,
          spaceAfter: 8,
        });
      }
      y = addReportTable(ctx, {
        columns: CUSTOM_CURVE_COLUMNS,
        rows: hypotheticalPortfolio.curveInputs || [],
      }, y);
    }
    y = addReportTable(ctx, {
      title: `${hypotheticalPortfolio.universeLabel} Calculation Detail`,
      columns: UST_COLUMNS,
      rows: hypotheticalPortfolio.rows || [],
      fontSize: 8,
      tableWidth: usableWidth,
      styles: { cellPadding: 3, overflow: "linebreak" },
    }, y);
    renderFundPlannerDisclosurePdf(ctx);
    ctx.finalize();
    const fileName = `FundPlanner_Structurer_${safeFilePart(report?.fundName || "Fund")}_${dayjs(report?.asOfDate || new Date()).format("YYYY-MM-DD")}.pdf`;
    if (save) doc.save(fileName);
    return { doc, fileName };
  }

  const stats = report?.stats;
  if (stats) {
    y = addSectionTitle(ctx, "Summary", y);
    y = addKeyValueBlock(ctx, [
      { label: "Total Cash Outflows", value: fmtCurrency(stats.totalCashOutflows, 2) },
      { label: "Weighted Avg Coupon", value: percent(stats.weightedAvgCoupon) },
      { label: "Number of Positions", value: fmtNumber(stats.numSecurities, 0) },
      { label: "Earliest Maturity", value: stats.earliestMaturity ? fmtDate(stats.earliestMaturity) : "—" },
      { label: "Latest Maturity", value: stats.latestMaturity ? fmtDate(stats.latestMaturity) : "—" },
      { label: "Weighted Avg Life", value: `${fmtNumber(stats.weightedAvgLifeYears, 3)} years` },
      { label: "Portfolio DV01", value: fmtCurrency(stats.dv01Total, 2) },
    ], y, { columns: 2, labelWidth: 125, rowHeight: 16, spaceAfter: 4 });

    y = addSectionTitle(ctx, "Performance", y, { spaceBefore: 12 });
    y = addKeyValueBlock(ctx, [
      { label: "Total Investment Cost", value: fmtCurrency(stats.totalInvested, 2) },
      { label: "Total Interest Earned", value: fmtCurrency(stats.totalInterest, 2) },
      { label: "Effective Portfolio Yield", value: percent(stats.portfolioXirrPct) },
    ], y, { columns: 2, labelWidth: 125, rowHeight: 16, spaceAfter: 4 });
  }

  y = addReportTable(ctx, { title: "Requirements", columns: DRAW_COLUMNS, rows: report?.draws || [] }, y);
  y = addStructurerScheduleChart(ctx, report?.chartData?.schedule || [], y);
  y = addReportTable(ctx, {
    title: "Investment Solution",
    columns: INVESTMENT_COLUMNS,
    rows: report?.investments || [],
    fontSize: 6.7,
    tableWidth: usableWidth,
    styles: { cellPadding: 2, overflow: "linebreak" },
  }, y);
  y = addStructurerSufficiencyChart(
    ctx,
    report?.chartData?.sufficiency || [],
    y,
    report?.fundMode
  );
  y = addReportTable(ctx, {
    title: "Fund Cash Flows",
    columns: SUFFICIENCY_COLUMNS,
    rows: report?.sufficiencyRows || [],
    fontSize: 6.8,
    styles: { cellPadding: 2, overflow: "linebreak" },
  }, y);

  renderFundPlannerDisclosurePdf(ctx);
  ctx.finalize();
  const fileName = `FundPlanner_Structurer_${safeFilePart(report?.fundName || "Fund")}_${dayjs(report?.asOfDate || new Date()).format("YYYY-MM-DD")}.pdf`;
  if (save) doc.save(fileName);
  return { doc, fileName };
}

export default exportStructurerPdf;
